<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiMenu from '@/Components/UI/UiMenu.vue'
import UiStatusPill from '@/Components/UI/UiStatusPill.vue'
import Icon from '@/Components/UI/Icon.vue'
import ConversationList from '@/Components/Inbox/ConversationList.vue'
import MessageThread    from '@/Components/Inbox/MessageThread.vue'
import Composer         from '@/Components/Inbox/Composer.vue'
import StatusDropdown   from '@/Components/Inbox/StatusDropdown.vue'
import AssignMenu       from '@/Components/Inbox/AssignMenu.vue'
import ContactAvatar    from '@/Components/Inbox/ContactAvatar.vue'
import ContextDrawer    from '@/Components/Inbox/ContextDrawer.vue'
import { useInboxStore } from '@/stores/useInboxStore'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { router, usePage } from '@inertiajs/vue3'

defineOptions({ layout: AppLayout })

const props = defineProps({
  conversations:     { type: Array,  required: true },
  inboxes:           { type: Array,  default: () => [] },
  assignable_users:  { type: Array,  default: () => [] },
  selected:          { type: Object, default: null },
  filters:           { type: Object, default: () => ({}) }
})

const { t } = useI18n()
const { isMobile } = useBreakpoint()
const page = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')

const store = useInboxStore()
const showDrawer = ref(false)
onMounted(() => store.loadQuickReplies())

// Three narrow watchers so a partial `:only=['selected']` reload doesn't
// re-sort and re-render the conversation list.
watch(
  () => props.conversations,
  (cs) => store.hydrateFromProps({ conversations: cs, filters: props.filters }),
  { immediate: true, deep: false },
)
watch(
  () => props.selected,
  (sel) => store.hydrateFromProps({ selected: sel ?? null }),
  { immediate: true, deep: false },
)
watch(
  () => props.filters,
  (f) => store.hydrateFromProps({ filters: f }),
  { deep: true },
)

const conversationList = computed(() => store.conversations)
const activeConversation = computed(() => store.selected)
const connectionMode = computed(() => store.connectionMode)

const pipelineOpts = computed(() => [
  { value: '',                label: t('inbox.filters.pipeline.all') },
  { value: 'new',             label: t('inbox.filters.pipeline.new') },
  { value: 'in_progress',     label: t('inbox.filters.pipeline.in_progress') },
  { value: 'rental_pending',  label: t('inbox.filters.pipeline.rental_pending') },
  { value: 'rental_active',   label: t('inbox.filters.pipeline.rental_active') },
  { value: 'rental_returned', label: t('inbox.filters.pipeline.rental_returned') },
  { value: 'resolved',        label: t('inbox.filters.pipeline.resolved') },
])

const channelLabel = computed(() => {
  const arr = (props.filters.channel || []).map(String)
  if (arr.length === 0) return t('inbox.filters.allChannels')
  if (arr.length === 1) {
    const found = props.inboxes.find((i) => String(i.id) === arr[0])
    return found ? found.name : arr[0]
  }
  return t('inbox.filters.nChannels', { n: arr.length })
})

const setQuery   = (v) => router.get('/inbox', { ...props.filters, q: v }, { preserveState: true, preserveScroll: true })
const setPipeline = (v) => router.get('/inbox', { ...props.filters, pipeline: v }, { preserveState: true })
const toggleUnread = () => {
  const next = props.filters.unread === '1' ? null : '1'
  router.get('/inbox', { ...props.filters, unread: next }, { preserveState: true })
}
const toggleChannel = (id) => {
  const current = Array.isArray(props.filters.channel) ? [ ...props.filters.channel ] : []
  const sid = String(id)
  const idx = current.indexOf(sid)
  if (idx === -1) current.push(sid); else current.splice(idx, 1)
  router.get('/inbox', { ...props.filters, channel: current }, { preserveState: true })
}
const syncing = ref(false)
const syncInboxes = async () => {
  if (syncing.value) return
  syncing.value = true
  try {
    await fetch('/inbox/inboxes/sync', {
      method: 'POST',
      headers: {
        'Accept':           'application/json',
        'X-CSRF-Token':     document.querySelector('meta[name="csrf-token"]')?.content || '',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
  } catch (e) {
    // refresh anyway -- the sync side-effect can fail without blocking a UI reload
  }
  router.reload({
    preserveScroll: true,
    onFinish: () => { syncing.value = false },
  })
}

const takeThis = async () => {
  if (!activeConversation.value) return
  await store.takeConversation(activeConversation.value.id)
}
const releaseThis = async () => {
  if (!activeConversation.value) return
  await store.releaseConversation(activeConversation.value.id)
}

const onStatusChanged = () => router.reload({ only: ['selected'] })
const onMessageSent = () => { /* store handled it; no-op */ }
const onAssigned = (detail) => {
  if (detail) store.setSelected(detail)
}
</script>

<template>
  <PageShell :title="t('inbox.title')">
    <template #filters>
      <UiInput
        :model-value="filters.q"
        :placeholder="t('inbox.filters.search')"
        class="flex-1 md:flex-none md:w-80"
        @update:model-value="setQuery"
      />
      <UiSegmented
        :model-value="filters.pipeline || ''"
        :options="pipelineOpts"
        @update:model-value="setPipeline"
      />
      <button
        type="button"
        class="inline-flex items-center gap-1 px-3 h-9 rounded-full text-xs border transition-colors"
        :class="filters.unread === '1'
          ? 'bg-[color:var(--accent)] text-[color:var(--accentInk)] border-[color:var(--accent)]'
          : 'bg-[color:var(--surface)] text-[color:var(--inkSoft)] border-[color:var(--line)]'"
        @click="toggleUnread"
      >
        <Icon
          name="dot"
          :size="10"
        /> {{ t('inbox.filters.unreadOnly') }}
      </button>
      <UiMenu v-if="inboxes.length > 1">
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="inline-flex items-center gap-1 px-3 h-9 rounded-full text-xs border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)]"
            @click="toggle"
          >
            <span>{{ channelLabel }}</span>
            <Icon
              name="chev-d"
              :size="12"
            />
          </button>
        </template>
        <label
          v-for="i in inboxes"
          :key="i.id"
          class="flex items-center gap-2 px-3 py-2 hover:bg-[color:var(--surfaceAlt)] text-sm cursor-pointer"
        >
          <input
            type="checkbox"
            :checked="(filters.channel || []).map(String).includes(String(i.id))"
            @change="toggleChannel(i.id)"
          >
          {{ i.name }}
        </label>
      </UiMenu>
      <UiStatusPill
        v-if="connectionMode === 'polling' || connectionMode === 'offline'"
        :tone="connectionMode === 'polling' ? 'warn' : 'muted'"
        :label="connectionMode === 'polling' ? t('inbox.connection.polling') : t('inbox.connection.offline')"
      />
      <button
        v-if="isAdmin"
        type="button"
        :disabled="syncing"
        :title="t('inbox.filters.sync')"
        :aria-label="t('inbox.filters.sync')"
        class="inline-flex items-center justify-center h-9 w-9 rounded-[var(--radius-button)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--inkSoft)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surfaceAlt)] transition-colors disabled:opacity-60 disabled:cursor-wait"
        @click="syncInboxes"
      >
        <Icon
          name="refresh"
          :size="16"
          :class="syncing ? 'animate-spin' : ''"
        />
      </button>
    </template>

    <div class="grid grid-cols-1 md:grid-cols-[320px_1fr] xl:grid-cols-[320px_1fr_320px] h-[calc(100vh-180px)] border border-[color:var(--line)] rounded-[var(--radius-lg)] overflow-hidden bg-[color:var(--surface)]">
      <ConversationList
        v-show="!isMobile || !activeConversation"
        :conversations="conversationList"
        :selected-id="activeConversation?.id"
      />

      <section
        v-if="activeConversation"
        class="flex flex-col h-full border-l border-[color:var(--line)]"
      >
        <header class="flex items-center gap-3 p-3 border-b border-[color:var(--line)] bg-[color:var(--surface)]">
          <ContactAvatar
            :avatar-url="activeConversation.contact_avatar"
            :name="activeConversation.customer_name || activeConversation.supplier_name || activeConversation.contact_name"
            :channel-type="activeConversation.channel_type"
            :tone="activeConversation.contact_kind === 'vehicle_source' ? 'supplier' : 'customer'"
            :size="40"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm text-[color:var(--ink)] truncate">
              {{ activeConversation.customer_name || activeConversation.contact_name || t('inbox.thread.unmatched') }}
            </p>
            <p class="text-xs text-[color:var(--muted)] truncate">
              {{ activeConversation.customer_id ? t('inbox.thread.customer') : t('inbox.thread.unmatched') }}
            </p>
          </div>
          <button
            v-if="activeConversation"
            type="button"
            class="xl:hidden inline-flex items-center justify-center h-9 w-9 rounded-[var(--radius-button)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--inkSoft)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surfaceAlt)]"
            :title="t('inbox.drawer.unmatched')"
            @click="showDrawer = true"
          >
            <Icon
              name="users"
              :size="16"
            />
          </button>
          <UiButton
            v-if="!activeConversation.taken_at"
            variant="primary"
            size="sm"
            @click="takeThis"
          >
            {{ t('inbox.thread.takeThis') }}
          </UiButton>
          <UiButton
            v-else-if="activeConversation.taken_at"
            variant="ghost"
            size="sm"
            @click="releaseThis"
          >
            {{ t('inbox.thread.release') }}
          </UiButton>
          <AssignMenu
            v-if="activeConversation.taken_at"
            :conversation-id="activeConversation.id"
            :taken-by-id="activeConversation.taken_by_id"
            :users="props.assignable_users"
            @changed="onAssigned"
          />
          <span
            v-if="activeConversation.taken_by_name && activeConversation.taken_at"
            class="text-xs text-[color:var(--muted)]"
          >
            {{ t('inbox.thread.takenBy', { name: activeConversation.taken_by_name }) }}
          </span>
          <StatusDropdown
            :conversation-id="activeConversation.id"
            :status="activeConversation.status"
            :pipeline-stage="activeConversation.pipeline_stage"
            :taken="!!activeConversation.taken_at"
            @changed="onStatusChanged"
          />
        </header>
        <MessageThread
          :messages="activeConversation.messages"
          :customer-id="activeConversation.customer_id"
        />
        <Composer
          :conversation-id="activeConversation.id"
          :taken="!!activeConversation.taken_at"
          @sent="onMessageSent"
        />
      </section>

      <section
        v-else
        class="hidden md:flex items-center justify-center text-[color:var(--muted)]"
      >
        <p>{{ t('inbox.empty') }}</p>
      </section>

      <ContextDrawer
        v-if="activeConversation"
        class="hidden xl:block"
        :conversation="activeConversation"
      />
    </div>

    <Teleport to="body">
      <Transition name="overlay">
        <div
          v-if="showDrawer && activeConversation"
          class="fixed inset-0 z-50 flex md:items-center md:justify-end md:p-4 bg-black/60"
          @click.self="showDrawer = false"
        >
          <div
            class="w-full h-full md:w-80 md:h-auto md:max-h-[calc(100vh-2rem)] md:rounded-[var(--radius-lg)]
                      bg-[color:var(--surface)] flex flex-col overflow-hidden"
          >
            <header class="flex items-center justify-between p-3 border-b border-[color:var(--line)]">
              <span class="text-sm fw-value">{{ activeConversation.customer_name || activeConversation.contact_name || t('inbox.thread.unmatched') }}</span>
              <button
                type="button"
                class="text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                @click="showDrawer = false"
              >
                <Icon
                  name="x"
                  :size="16"
                />
              </button>
            </header>
            <ContextDrawer
              :conversation="activeConversation"
              class="flex-1 border-0"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </PageShell>
</template>
